import React from 'react';

import type { Ground } from '../hooks/useGround';
import { useGround } from '../hooks/useGround';

/**
 * The hub's ground switch — the same control the public site carries, rebuilt
 * for React and, like the public site's, riding the bottom-right corner of the
 * viewport. It sat in the top bar until 2026-08-09; the bar is for navigation,
 * and a preference set once a year does not need to hold width there.
 *
 * It carries `bg-page` because it now floats over content rather than sitting in
 * chrome that already had a ground. Without it the control would be a hairline
 * box with paragraphs running through it.
 *
 * This is what the hub gets *instead of* a dark-mode toggle, which was open item
 * 6 in docs/pages-content-frontend-handover.md §9. Adding a dark toggle beside a
 * ground switch would have been two controls over one axis, and it would encode
 * the light/dark relationship DESIGN.md says these two grounds do not have.
 *
 * The mark is the brand pattern in miniature: a sheet with two contour lines
 * across it, drawn in ink on paper and in light on navy.
 */

interface GroundOption {
  value: Ground;
  label: string;
  /** True for the navy mark, which fills the sheet and lightens the contours. */
  inverted?: boolean;
}

const OPTIONS: GroundOption[] = [
  { value: 'paper', label: 'Paper' },
  { value: 'navy', label: 'Navy', inverted: true },
];

const GroundMark: React.FC<{ inverted?: boolean }> = ({ inverted }) => (
  <svg
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
    className="h-3.5 w-3.5 shrink-0 fill-none stroke-current"
    strokeWidth={1.2}
    strokeLinecap="round"
  >
    <path d="M2.5 3.5h11v9h-11z" className={inverted ? 'fill-current opacity-90' : undefined} />
    <path
      d="M2.5 6.2c2.6.9 4.2-.9 5.6 0s2.9.9 5.4 0M2.5 9.4c2.6.9 4.2-.9 5.6 0s2.9.9 5.4 0"
      className={inverted ? 'opacity-45' : undefined}
    />
  </svg>
);

const GroundSwitch: React.FC = () => {
  const { ground, setGround } = useGround();

  return (
    <div
      role="group"
      aria-label="Page ground"
      className="flex shrink-0 items-center gap-0.5 rounded-sheet border border-rule-strong bg-page p-0.5"
    >
      {OPTIONS.map((option) => {
        const selected = ground === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => setGround(option.value)}
            title={`${option.label} ground`}
            className={`flex items-center gap-1.5 rounded-sheet px-2 py-1.5 font-ui text-step--2 font-medium uppercase tracking-[0.11em] transition-colors duration-fast ease-sheet ${
              // Selected reads as an ink inversion, never as the accent. Apricot
              // means "the pointer is here" or "this is the action"; a permanent
              // orange chip in the chrome is neither, and it would compete with
              // the one thing on screen actually asking to be clicked.
              selected
                ? 'bg-ink text-page'
                : 'text-ink-muted hover:text-ink focus-visible:text-ink'
            }`}
          >
            <GroundMark inverted={option.inverted} />
            {/* The word is the affordance on a wide bar; once the bar tightens
                the mark carries it alone and the label would only crowd. */}
            <span className="hidden lg:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default GroundSwitch;
